import './promotionAlerts.scss';

import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Alert } from '@eliberty/design-system-react';
import classNames from 'classnames';

import { Promotion } from '~types/appsync.types';

interface PromotionAlertsProps {
  /** List of promotions coming from the simulation result */
  promotions?: Promotion[];
  /** Optional custom className to align styles with parent layout */
  className?: string;
  /** Control whether promotion description should be displayed */
  showDescriptions?: boolean;
  /** When true, prepends a dynamic discount promotion with a fire icon */
  hasDynamicDiscount?: boolean;
}

const DYNAMIC_DISCOUNT_ID = Number.MIN_SAFE_INTEGER;

const PromotionAlerts = ({
  promotions,
  className,
  showDescriptions = true,
  hasDynamicDiscount = false,
}: PromotionAlertsProps) => {
  const { formatMessage } = useIntl();

  const allPromotions = useMemo(() => {
    const base = promotions ?? [];
    if (!hasDynamicDiscount) return base;
    const dynamicPromotion: Promotion = {
      __typename: 'Promotion',
      description: '',
      id: DYNAMIC_DISCOUNT_ID,
      name: formatMessage({
        defaultMessage: "You're paying less online!",
        id: 'dynamicPriceDiscount',
      }),
    };
    return [dynamicPromotion, ...base];
  }, [promotions, hasDynamicDiscount, formatMessage]);

  if (allPromotions.length === 0) {
    return null;
  }

  const hasOnlyDynamicDiscount = hasDynamicDiscount && (!promotions || promotions.length === 0);
  const namesOnly = !showDescriptions;
  const inlineNamesLabel = allPromotions.map(({ name }) => name).join(', ');
  const appliedPromotionsLabel = formatMessage({
    defaultMessage: 'Applied promotions',
    id: 'appliedPromotions',
  });

  const title = hasOnlyDynamicDiscount ? inlineNamesLabel : appliedPromotionsLabel;

  return (
    <div
      className={classNames('PromotionAlerts', className, {
        'PromotionAlerts--dynamicDiscount': hasOnlyDynamicDiscount,
        'PromotionAlerts--namesOnly': namesOnly && !hasOnlyDynamicDiscount,
      })}
    >
      <Alert icon='interface-promo' className='PromotionAlerts_alert' title={title}>
        {!hasOnlyDynamicDiscount && namesOnly && (
          <span className='PromotionAlerts_inlineList'>{inlineNamesLabel}</span>
        )}
        {!hasOnlyDynamicDiscount && !namesOnly && (
          <div className='PromotionAlerts_list'>
            {allPromotions.map((promotion) => (
              <p key={promotion.id ?? promotion.name} className='PromotionAlerts_item'>
                <span className='PromotionAlerts_itemName'>{promotion.name}</span>
                {promotion.description && showDescriptions ? (
                  <span className='PromotionAlerts_itemDescription'>
                    {` : ${promotion.description}`}
                  </span>
                ) : null}
              </p>
            ))}
          </div>
        )}
      </Alert>
    </div>
  );
};

export default PromotionAlerts;
